import axios from 'axios';
import axiosRetry from 'axios-retry';
import { uri } from '../../utils/config';
import moment from 'moment';

const client = axios.create({
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  },
});
axiosRetry(client, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
});

class Auth {
  static baseUrl = uri.auth;

  static timeout = null;

  static setAccessToken = (accessToken) => {
    localStorage.setItem('up_portal_access_token', accessToken);
  };

  static getAccessToken = () => {
    const accessToken = localStorage.getItem('up_portal_access_token');
    return accessToken;
  };

  static setRefreshToken = (refreshToken) => {
    localStorage.setItem('up_portal_refresh_token', refreshToken);
  };

  static getRefreshToken = () => {
    const refreshToken = localStorage.getItem('up_portal_refresh_token');
    return refreshToken;
  };

  static removeRefreshToken = () => {
    localStorage.removeItem('up_portal_refresh_token');
  };

  static reloadOnExpiry = (refreshToken) => {
    if (!refreshToken) return;
    const token = Auth.parseJwt(refreshToken);
    if (!token?.exp) return;
    const { exp } = token;
    const now = moment().utc().valueOf();
    const expireTime = exp * 1000;
    if (expireTime > now) {
      clearTimeout(Auth.timeout);
      Auth.timeout = window.setTimeout(() => {
        window.location.reload()
      }, 301000);
    } else {
      window.location.reload();
    }
  }

  static parseJwt = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  };

  static login = async (email, password) => {
    try {
      const requestBody = { email, password };

      const response = await axios.post(
        `${this.baseUrl}/login`,
        requestBody,
      );
      const { data: loginResponse } = response;

      const { accessToken, refreshToken, error, message } = loginResponse;
      const missingTokens = accessToken === null || refreshToken === null;
      if (error || missingTokens) {
        return { success: false, user: null, error: message };
      }

      const { user } = this.parseJwt(accessToken);

      this.setAccessToken(accessToken);
      this.setRefreshToken(refreshToken);
      Auth.reloadOnExpiry(refreshToken);
      return { success: true, user, error: null };
    } catch (error) {
      if (error?.response) {
        return { success: false, user: null, error: error?.response?.data?.message }
      }

      return { success: false, user: null, error: 'Could not log you in, try again in a few minutes.' };
    }
  };

  static logout = async () => {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return { success: false };

      if (refreshToken) {
        const requestBody = { refreshToken };

        const { data: loginResponse } = await axios.post(
          `${this.baseUrl}/logout`,
          requestBody,
        );
        // get tokens from server response
        const { error } = loginResponse;

        if (error) {
          return { success: false };
        }
        clearTimeout(Auth.timeout);
        this.removeRefreshToken();
      }
      console.log('LOGOUT');
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  static refreshAccessToken = async () => {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) return { accessToken: null, error: true, message: 'Cannot request access token' };

      const requestBody = { refreshToken };
      const { data: accessTokenResponse } = await axios.post(
        `${this.baseUrl}/access_token`,
        requestBody,
      );

      const { accessToken, error, message } = accessTokenResponse;
      if (!accessToken || error) return { accessToken: null, error: true, message };

      this.setAccessToken(accessToken);
      return { accessToken, error: false, message };
    } catch (error) {
      if (error?.response) {
        return { accessToken: null, error: true, message: error?.response?.data?.message }
      }

      return { accessToken: null, error: true, message: 'Error could not get access token' };
    }
  };

  static loginWithRefreshToken = async () => {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) return { success: false, user: null, message: '' };

      const requestBody = { refreshToken };
      const { data: accessTokenResponse } = await axios.post(
        `${this.baseUrl}/access_token`,
        requestBody,
      );

      const { accessToken, error, message } = accessTokenResponse;
      if (!accessToken || error) {
        return { success: false, user: null, message: message };
      }

      const { user } = this.parseJwt(accessToken);

      this.setAccessToken(accessToken);
      return { success: true, user, message };
    } catch (error) {
      if (error?.response) {
        return { success: false, user: null, message: error?.response?.data?.message }
      }

      return { success: false, user: null, message: 'Error could not log you in at the moment' };
    }
  };
}

export { Auth };
